import { COUNTRY_OPTIONS } from "./countries";
import CityInput from "./CityInput";
import React, { useEffect, useRef, useState } from "react";
import { BackButton, Button, Input, Logo } from "./ui";

/* -------------------------------------------------------------------------- */
/* Shared wrapper for auth screens (register / login).                        */
/* -------------------------------------------------------------------------- */

function AuthShell({ onBack, children }) {
  return (
    <div className="min-h-screen bg-[#FBFCFF]" data-testid="auth-shell">
      <div className="mx-auto flex max-w-xl items-center justify-between px-5 py-5">
        <BackButton onClick={onBack} />
        <Logo size="sm" />
      </div>
      <div className="mx-auto max-w-xl px-5 pb-[calc(env(safe-area-inset-bottom)+7rem)]">{children}</div>
    </div>
  );
}

function ServerError({ message }) {
  if (!message) return null;
  return (
    <div
      className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm font-semibold text-rose-700"
      data-testid="auth-server-error"
    >
      {message}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Register                                                                   */
/* -------------------------------------------------------------------------- */

export function Register({ onBack, onSubmit }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    city: "",
    country_code: "PA",
    acceptTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Ingresa tu nombre completo.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Ingresa un correo válido.";
    if (form.password.length < 6)
      e.password = "La contraseña debe tener al menos 6 caracteres.";
    if (!form.city.trim()) e.city = "Selecciona o escribe tu ciudad.";
    if (!form.acceptTerms)
      e.acceptTerms = "Debes aceptar los términos para continuar.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setLoading(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setServerError(err.message || "No pudimos crear tu cuenta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell onBack={onBack}>
      <div className="pc-rise">
        <h1 className="font-display text-4xl font-black leading-tight text-[#0B2D4D] sm:text-5xl">
          Crea tu cuenta en <span className="text-[#FF5C5C]">PawCita</span>
        </h1>
        <p className="mt-3 text-base text-slate-500">
          Únete a la comunidad de padres de perros responsables.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
          <Input
            label="Nombre completo"
            placeholder="Tu nombre completo"
            value={form.fullName}
            onChange={(e) => update("fullName", e.target.value)}
            error={errors.fullName}
            testId="register-name-input"
            autoComplete="name"
          />
          <Input
            label="Correo electrónico"
            type="email"
            placeholder="tu@correo.com"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            error={errors.email}
            testId="register-email-input"
            autoComplete="email"
          />
          <Input
            label="Contraseña"
            type="password"
            placeholder="Mínimo 6 caracteres"
            value={form.password}
            onChange={(e) => update("password", e.target.value)}
            error={errors.password}
            testId="register-password-input"
            autoComplete="new-password"
          />
          <label className="block">
            <span className="mb-1.5 block text-sm font-bold text-[#0B2D4D]">
              País donde usas PawCita
            </span>
            <select
              value={form.country_code}
              onChange={(e) => setForm((current) => ({ ...current, country_code: e.target.value, city: "" }))}
              data-testid="register-country-select"
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[#0B2D4D] outline-none focus:border-[#67D5B5]"
            >
              {COUNTRY_OPTIONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          </label>
          <CityInput
            countryCode={form.country_code}
            value={form.city}
            onChange={(value) => update("city", value)}
            label="Ciudad"
            testId="register-city-input"
            error={errors.city}
          />

          <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-100 bg-white p-3">
            <input
              type="checkbox"
              checked={form.acceptTerms}
              onChange={(e) => update("acceptTerms", e.target.checked)}
              data-testid="register-terms-checkbox"
              className="mt-1 h-5 w-5 accent-[#67D5B5]"
            />
            <span className="text-sm text-slate-600">
              Acepto los <span className="font-bold text-[#0B2D4D]">términos y condiciones</span> y la política de privacidad de PawCita.
            </span>
          </label>
          {errors.acceptTerms && (
            <p className="text-xs font-semibold text-rose-600">
              {errors.acceptTerms}
            </p>
          )}

          <ServerError message={serverError} />

          <Button
            variant="primary"
            type="submit"
            testId="register-submit-btn"
            disabled={loading}
            className="mt-2 w-full py-4 text-base"
          >
            {loading ? "Creando cuenta…" : "Crear mi cuenta"}
          </Button>

          <p className="pt-2 text-center text-xs text-slate-400">
            PawCita protege tu información. Nunca compartiremos tu correo.
          </p>
        </form>
      </div>
    </AuthShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Verify email                                                               */
/* -------------------------------------------------------------------------- */

export function VerifyEmail({ email, devCode, mode, onVerify, onResend, onLogout }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [shownCode, setShownCode] = useState(devCode || "");
  const autoTried = useRef(false);

  // If we arrived without a code (e.g. via login), request one once.
  useEffect(() => {
    if (!shownCode && mode !== "twilio" && !autoTried.current) {
      autoTried.current = true;
      onResend()
        .then((r) => {
          if (r && r.dev_code) setShownCode(r.dev_code);
        })
        .catch(() =>
          setInfo("Ya te enviamos un código a tu correo. Revísalo o toca “Reenviar código”.")
        );
    }
  }, [shownCode, mode, onResend]);

  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    setInfo("");
    if (!/^\d{4,8}$/.test(code.trim())) {
      setError("Ingresa el código que te enviamos.");
      return;
    }
    setLoading(true);
    try {
      await onVerify(code.trim());
    } catch (err) {
      setError(err.message || "No pudimos verificar el código.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError("");
    setInfo("");
    setResending(true);
    try {
      const r = await onResend();
      if (r && r.dev_code) setShownCode(r.dev_code);
      setInfo("Te enviamos un nuevo código 🐾");
    } catch (err) {
      setError(err.message || "No pudimos reenviar el código.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFCFF]" data-testid="verify-email-screen">
      <div className="mx-auto flex max-w-xl items-center justify-between px-5 py-5">
        <Logo size="sm" />
        <button
          type="button"
          onClick={onLogout}
          data-testid="verify-logout-btn"
          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-[#0B2D4D] transition hover:bg-slate-50"
        >
          Cerrar sesión
        </button>
      </div>
      <div className="mx-auto max-w-xl px-5 pb-[calc(env(safe-area-inset-bottom)+7rem)]">
        <div className="pc-rise">
          <div className="text-4xl">📬</div>
          <h1 className="mt-3 font-display text-4xl font-black leading-tight text-[#0B2D4D] sm:text-5xl">
            Verifica tu correo 🐾
          </h1>
          <p className="mt-3 text-base text-slate-500">
            Te enviamos un código a tu correo. Ingrésalo para verificar tu cuenta.
          </p>
          <p className="mt-1 text-sm font-bold text-[#0B2D4D]" data-testid="verify-email-address">
            {email}
          </p>

          {shownCode && (
            <div
              className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
              data-testid="verify-dev-code"
            >
              <span className="font-bold">Modo desarrollo (mock):</span> tu código de prueba es{" "}
              <span className="font-black tracking-widest">{shownCode}</span>. En producción llegará
              por correo y no se mostrará aquí.
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
            <Input
              label="Código de verificación"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Ej. 123456"
              inputMode="numeric"
              testId="verify-code-input"
            />
            {error && (
              <p className="text-sm font-semibold text-rose-600" data-testid="verify-error">
                {error}
              </p>
            )}
            {info && (
              <p className="text-sm font-semibold text-emerald-600" data-testid="verify-info">
                {info}
              </p>
            )}
            <Button
              variant="primary"
              type="submit"
              testId="verify-submit-btn"
              disabled={loading}
              className="w-full py-4 text-base"
            >
              {loading ? "Verificando…" : "Verificar correo"}
            </Button>
          </form>

          <div className="mt-5 flex items-center justify-between">
            <button
              type="button"
              onClick={resend}
              disabled={resending}
              data-testid="verify-resend-btn"
              className="text-sm font-bold text-[#67D5B5] underline-offset-2 hover:underline disabled:opacity-50"
            >
              {resending ? "Reenviando…" : "Reenviar código"}
            </button>
            <span className="text-xs text-slate-400">Revisa también tu carpeta de spam</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Login({ onBack, onSubmit, onForgot }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    const e = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Ingresa un correo válido.";
    if (form.password.length < 6)
      e.password = "La contraseña debe tener al menos 6 caracteres.";
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setLoading(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setServerError(err.message || "No pudimos iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell onBack={onBack}>
      <div className="pc-rise">
        <h1 className="font-display text-4xl font-black leading-tight text-[#0B2D4D] sm:text-5xl">
          Bienvenido de vuelta 🐾
        </h1>
        <p className="mt-3 text-base text-slate-500">
          Entra a PawCita para seguir conectando, cuidando y protegiendo a tu perro.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
          <Input
            label="Correo electrónico"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            error={errors.email}
            placeholder="tu@correo.com"
            testId="login-email-input"
            autoComplete="email"
          />
          <Input
            label="Contraseña"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            error={errors.password}
            placeholder="Tu contraseña"
            testId="login-password-input"
            autoComplete="current-password"
          />

          <ServerError message={serverError} />

          <Button
            variant="primary"
            type="submit"
            testId="login-submit-btn"
            disabled={loading}
            className="mt-2 w-full py-4 text-base"
          >
            {loading ? "Entrando…" : "Entrar"}
          </Button>
          <p className="pt-2 text-center text-xs text-slate-400">
            Tu sesión se sincroniza en todos tus dispositivos.
          </p>
          <button
            type="button"
            onClick={onForgot}
            data-testid="login-forgot-btn"
            className="mx-auto block text-sm font-bold text-[#67D5B5] underline-offset-2 hover:underline"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </form>
      </div>
    </AuthShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Forgot / reset password                                                    */
/* -------------------------------------------------------------------------- */

export function ForgotPassword({ onBack, onRequestCode, onReset }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [devCode, setDevCode] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (ev) => {
    ev.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Ingresa un correo válido.");
      return;
    }
    setLoading(true);
    try {
      const r = await onRequestCode(email.trim());
      if (r && r.dev_code) setDevCode(r.dev_code);
      setStep(2);
    } catch (err) {
      setError(err.message || "No pudimos procesar la solicitud.");
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (ev) => {
    ev.preventDefault();
    setError("");
    if (!/^\d{4,8}$/.test(code.trim())) {
      setError("Ingresa el código que te enviamos.");
      return;
    }
    if (password.length < 6) {
      setError("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }
    setLoading(true);
    try {
      await onReset(email.trim(), code.trim(), password);
      setOk("Contraseña actualizada. Ahora puedes iniciar sesión.");
    } catch (err) {
      setError(err.message || "No pudimos actualizar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell onBack={onBack}>
      <div className="pc-rise" data-testid="forgot-screen">
        <div className="text-4xl">🔑</div>
        <h1 className="mt-3 font-display text-4xl font-black leading-tight text-[#0B2D4D] sm:text-5xl">
          Recuperar contraseña
        </h1>

        {ok ? (
          <div className="mt-6" data-testid="forgot-success">
            <p className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700">
              {ok}
            </p>
            <Button variant="primary" onClick={onBack} testId="forgot-back-to-login-btn" className="mt-4 w-full py-4">
              Ir a iniciar sesión
            </Button>
          </div>
        ) : step === 1 ? (
          <form onSubmit={requestCode} className="mt-6 space-y-4" noValidate>
            <p className="text-base text-slate-500">
              Te enviaremos un código para recuperar tu cuenta PawCita 🐾
            </p>
            <Input
              label="Correo electrónico"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@correo.com"
              testId="forgot-email-input"
            />
            {error && <p className="text-sm font-semibold text-rose-600" data-testid="forgot-error">{error}</p>}
            <Button variant="primary" type="submit" testId="forgot-send-btn" disabled={loading} className="w-full py-4">
              {loading ? "Enviando…" : "Enviar código"}
            </Button>
          </form>
        ) : (
          <form onSubmit={doReset} className="mt-6 space-y-4" noValidate>
            <p className="text-base text-slate-500">
              Ingresa el código que enviamos a <span className="font-bold text-[#0B2D4D]">{email}</span> y tu nueva contraseña.
            </p>
            {devCode && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="forgot-dev-code">
                <span className="font-bold">Modo desarrollo:</span> código de prueba{" "}
                <span className="font-black tracking-widest">{devCode}</span>.
              </div>
            )}
            <Input label="Código" value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="Ej. 123456" inputMode="numeric" testId="forgot-code-input" />
            <Input label="Nueva contraseña" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" testId="forgot-newpass-input" />
            {error && <p className="text-sm font-semibold text-rose-600" data-testid="forgot-error">{error}</p>}
            <Button variant="primary" type="submit" testId="forgot-reset-btn" disabled={loading} className="w-full py-4">
              {loading ? "Actualizando…" : "Cambiar contraseña"}
            </Button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
